import { LogData, MetricData, SessionStatsData } from '@workday/upc-common';
import { AnyAction, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { emptySplitApi } from './api.slice';
import { apiBaseUrl } from '../utils/urls';
import { Authenticator, ContactType, ServerErrorAction } from './types';
import { isFetchBaseQueryError, isServerErrorAction } from '../utils/server-error';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';

export type VerifyTotpOperation = 'change-password' | 'gdpr-delete';

export type VerifyPasswordOperation = 'gdpr-delete' | 'change-authenticator';

// These paths are exclusively used by the account module
export const AccountApiPaths = {
    userInfo: '/account/v1/public/userInfo',
    userSettings: '/account/v1/public/userSettings',
    userApplications: '/account/v1/public/userApplications/',
    // user alias endpoints
    userAliases: '/account/v1/public/alias',
    addAlias: '/account/v1/public/alias/add',
    verifyAliasOtp: '/account/v1/public/alias/verify',
    makePrimaryAlias: '/account/v1/public/alias/primary',
    // user info endpoints
    updateName: '/account/v1/public/user/name',
    uploadProfileImage: '/account/v1/public/userImage',

    getAuthenticator: '/account/v1/public/authenticator',
    verifyPasswordToUpdateAuthenticator: '/account/v1/public/authenticator/verifyPassword',
    initNewAuthenticator: '/account/v1/public/authenticator/init',
    updateAuthenticator: '/account/v1/public/authenticator/update',
    verifyAuthenticatorFromSettings: '/account/v1/public/authenticator/verify',
    activeUserSessions: '/account/v1/public/sessions',
    endAllActiveUserSessions: '/account/v1/public/sessions',
    initLostAuthenticator: '/account/v1/public/authenticator/registernew',
    updateLostAuthenticator: '/account/v1/public/authenticator/verifyNew',

    // update phone number
    verifyPasswordToUpdatePhone: '/account/v1/public/phone/verifyPassword',
    initUpdatePhone: '/account/v1/public/phone/init',
    updatePhoneVerifyOtp: '/account/v1/public/phone/verifyOtp/update',

    updateUserLocale: '/account/v1/public/userLocale',
    getAvailableLocales: '/account/v1/public/availableLocales',

    changePassword: '/account/v1/public/changepassword',
    changePasswordVerifyTotp: '/account/v1/public/changepassword/totp/submit',
    gdprDeleteVerifyPassword: '/account/v1/public/gdpr/verify',
    gdprDeleteVerifyTotp: '/account/v1/public/gdpr/totp/submit',
    gdprDeleteUser: '/account/v1/public/gdpr/deleteuser',
    userImage: '/account/v1/public/userImage',

    signOutUser: '/auth/v1/public/logout',
    getSsoUrl: '/auth/v1/public/sso/create',
    verifyUri: '/account/v1/public/verifyUri'
};

export const UiApiPaths = {
    logs: '/ui/v1/logs',
    stats: '/ui/v1/stats',
    metrics: '/ui/v1/metrics'
};

export const USER_IMAGE_URL = `${apiBaseUrl}${AccountApiPaths.uploadProfileImage}`;

export interface UserInfoResponse {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    isPhoneVerified: boolean;
    locale: string;
    accountResetDate?: string;
}

export interface Application {
    appId: string;
    clientId: string;
    customer: string;
    name: string;
    environment?: string;
    environmentType?: string;
    url: string;
    isEnvApp: boolean;
    tosUri?: string;
    privacyUri?: string;
    tenantName?: string;
    vcrRestricted: boolean;
    sandBoxed: boolean;
    expired: boolean;
    userExpired: boolean;
    userLocked: boolean;
    isFedRAMP?: boolean;
}

export interface ApplicationsResponse {
    tiles: Application[] | [];
}

interface GetSsoUrlResponse {
    redirectUri: string;
}

export type UserAliasType = 'EMAIL';

export interface UserAlias {
    alias: string;
    type: UserAliasType;
    primary: boolean;
}

export interface UserAliasesResponse {
    data: Array<UserAlias>;
}

const verifyTotpOperationsByPath: Record<VerifyTotpOperation, string> = {
    'change-password': AccountApiPaths.changePasswordVerifyTotp,
    'gdpr-delete': AccountApiPaths.gdprDeleteVerifyTotp
};

const verifyPasswordOperationsByPath: Record<VerifyPasswordOperation, string> = {
    'change-authenticator': AccountApiPaths.verifyPasswordToUpdateAuthenticator,
    'gdpr-delete': AccountApiPaths.gdprDeleteVerifyPassword
};

export const accountApiSlice = emptySplitApi
    .enhanceEndpoints({ addTagTypes: ['UserInfo', 'UserAlias'] })
    .injectEndpoints({
        endpoints: builder => ({
            verifyPassword: builder.mutation<any, { password: string; operation: VerifyPasswordOperation }>({
                query: ({ password, operation }) => ({
                    method: 'post',
                    url: verifyPasswordOperationsByPath[operation],
                    body: {
                        password
                    }
                })
            }),
            signOutUser: builder.mutation<any, void>({
                query: () => ({
                    method: 'post',
                    url: AccountApiPaths.signOutUser
                })
            }),
            verifyTotp: builder.mutation<any, { passcode: string; operation: VerifyTotpOperation }>({
                query: ({ passcode, operation }) => ({
                    method: 'post',
                    url: verifyTotpOperationsByPath[operation],
                    body: {
                        passcode
                    }
                })
            }),
            getAuthenticator: builder.query<Authenticator, void>({
                query: () => AccountApiPaths.getAuthenticator
            }),
            initNewAuthenticator: builder.query<Authenticator, void>({
                query: () => AccountApiPaths.initNewAuthenticator
            }),
            updateAuthenticator: builder.mutation({
                query: body => ({
                    body,
                    method: 'put',
                    url: AccountApiPaths.updateAuthenticator
                })
            }),
            verifyAuthenticatorFromSettings: builder.mutation({
                query: body => ({
                    body,
                    method: 'post',
                    url: AccountApiPaths.verifyAuthenticatorFromSettings
                })
            }),
            activeUserSessions: builder.query({
                query: () => AccountApiPaths.activeUserSessions
            }),
            endAllActiveUserSessions: builder.mutation<void, void>({
                query: body => ({
                    body,
                    method: 'post',
                    url: AccountApiPaths.endAllActiveUserSessions
                })
            }),
            initLostAuthenticator: builder.mutation({
                query: body => ({
                    body,
                    method: 'post',
                    url: AccountApiPaths.initLostAuthenticator
                })
            }),
            updateLostAuthenticator: builder.mutation({
                query: body => ({
                    body,
                    method: 'post',
                    url: AccountApiPaths.updateLostAuthenticator
                })
            }),
            verifyPasswordToUpdatePhone: builder.mutation({
                query: body => ({
                    body,
                    method: 'post',
                    url: AccountApiPaths.verifyPasswordToUpdatePhone
                })
            }),
            initUpdatePhone: builder.mutation({
                query: ({ countryCode, number }) => ({
                    body: {
                        countryCode,
                        number
                    },
                    method: 'post',
                    url: AccountApiPaths.initUpdatePhone
                })
            }),
            updatePhoneVerifyOtp: builder.mutation({
                query: body => ({
                    body,
                    method: 'post',
                    url: AccountApiPaths.updatePhoneVerifyOtp
                }),
                invalidatesTags: ['UserInfo']
            }),
            getUserInfo: builder.query<UserInfoResponse, void>({
                query: () => AccountApiPaths.userInfo,
                transformResponse: (response: any) => {
                    return {
                        firstName: response.given_name || response.firstName,
                        lastName: response.family_name || response.lastName,
                        email: response.email,
                        phoneNumber: response.phone_number || response.phoneNumber,
                        isPhoneVerified: response.is_phone_verified || response.isPhoneVerified,
                        locale: response.locale,
                        accountResetDate: response.account_reset_date || response.accountResetDate
                    };
                },
                providesTags: ['UserInfo']
            }),
            getUserSettings: builder.query({
                query: () => AccountApiPaths.userSettings,
                keepUnusedDataFor: 0
            }),
            updateUserSettings: builder.mutation({
                query: body => ({
                    body,
                    method: 'put',
                    url: AccountApiPaths.userSettings
                })
            }),
            getAvailableLocales: builder.query({
                query: () => AccountApiPaths.getAvailableLocales,
                keepUnusedDataFor: 0
            }),
            updateUserLocale: builder.mutation({
                query: body => ({
                    body,
                    method: 'put',
                    url: AccountApiPaths.updateUserLocale
                }),
                invalidatesTags: ['UserInfo']
            }),
            getApplications: builder.query<ApplicationsResponse, void>({
                query: () => AccountApiPaths.userApplications
            }),
            gdprDeleteUser: builder.mutation<any, void>({
                query: body => ({
                    body,
                    method: 'post',
                    url: AccountApiPaths.gdprDeleteUser
                })
            }),
            mfaInit: builder.mutation<void, { contactId: string; contactType: ContactType }>({
                query: ({ contactId, contactType }) => ({
                    method: 'post',
                    url: `/auth/v1/mfa/${contactType}/init`,
                    body: { contactId }
                })
            }),
            mfaVerify: builder.mutation<void, { contactId: string; contactType: ContactType; passcode: string }>({
                query: ({ contactId, contactType, passcode }) => ({
                    method: 'post',
                    url: `/auth/v1/mfa/${contactType}/verify`,
                    body: { contactId, passcode }
                })
            }),
            changePassword: builder.mutation({
                query: ({ oldPassword, newPassword }) => ({
                    method: 'post',
                    url: AccountApiPaths.changePassword,
                    body: {
                        oldPassword,
                        newPassword
                    }
                })
            }),
            updateName: builder.mutation({
                query: ({ firstName, lastName }) => ({
                    method: 'post',
                    url: AccountApiPaths.updateName,
                    body: { firstName, lastName }
                }),
                invalidatesTags: ['UserInfo']
            }),
            fetchUserAliases: builder.query<UserAliasesResponse, void>({
                query: () => AccountApiPaths.userAliases,
                providesTags: ['UserAlias']
            }),
            addAlias: builder.mutation({
                query: ({ alias, type, primary }) => ({
                    method: 'post',
                    url: AccountApiPaths.addAlias,
                    body: { alias, type, primary }
                })
            }),
            verifyAliasOtp: builder.mutation<void, { passcode: string }>({
                query: ({ passcode }) => ({
                    method: 'post',
                    url: AccountApiPaths.verifyAliasOtp,
                    body: { otp: passcode }
                }),
                invalidatesTags: ['UserAlias']
            }),
            addAliasAsPrimary: builder.mutation({
                query: ({ alias, type, primary }) => ({
                    method: 'put',
                    url: AccountApiPaths.makePrimaryAlias,
                    body: { alias, type, primary }
                }),
                invalidatesTags: ['UserInfo', 'UserAlias']
            }),
            deleteAlias: builder.mutation({
                query: ({ alias, type }) => ({
                    method: 'delete',
                    url: AccountApiPaths.userAliases,
                    body: { alias, type }
                }),
                invalidatesTags: ['UserAlias']
            }),
            uploadProfileImage: builder.mutation({
                query: ({ formData }) => ({
                    method: 'post',
                    url: AccountApiPaths.uploadProfileImage,
                    body: formData
                })
            }),
            sendLog: builder.mutation<void, LogData>({
                query: body => ({
                    body,
                    method: 'post',
                    url: UiApiPaths.logs
                })
            }),
            sendSessionStats: builder.mutation<void, SessionStatsData>({
                query: body => ({
                    body,
                    method: 'post',
                    url: UiApiPaths.stats
                })
            }),
            sendMetric: builder.mutation<void, MetricData>({
                query: body => ({
                    body,
                    method: 'post',
                    url: UiApiPaths.metrics
                })
            }),
            getSsoUrl: builder.query<GetSsoUrlResponse, { applicationId: string; isEnvApp: boolean }>({
                query: ({ applicationId, isEnvApp }) => ({
                    method: 'get',
                    url: AccountApiPaths.getSsoUrl,
                    params: {
                        applicationId,
                        isEnvApp
                    }
                }),
                keepUnusedDataFor: 0
            }),
            verifyUri: builder.mutation<{ isValid: boolean }, { redirectUri: string; clientId: string }>({
                query: ({ redirectUri, clientId }) => ({
                    body: {
                        redirectUri,
                        clientId
                    },
                    method: 'post',
                    url: AccountApiPaths.verifyUri
                })
            })
        })
    });

const is401UnauthorizedMatch = (action: AnyAction): action is ServerErrorAction => {
    return (
        isServerErrorAction(action) && action.payload?.data?.error === 'Unauthorized' && action.payload?.status === 401
    );
};

const is403Match = (action: AnyAction): action is PayloadAction<FetchBaseQueryError> => {
    // no data on this, so it is not a ServerErrorAction
    return isFetchBaseQueryError(action?.payload) && action.payload?.status === 403;
};

const isRegularLogoutMatch = isAnyOf(accountApiSlice.endpoints.gdprDeleteUser.matchFulfilled, is401UnauthorizedMatch);

const isLogoutWithNoticeMatch = isAnyOf(
    accountApiSlice.endpoints.signOutUser.matchFulfilled,
    accountApiSlice.endpoints.endAllActiveUserSessions.matchFulfilled,
    is403Match
);

const isAnyLogoutMatch = isAnyOf(isRegularLogoutMatch, isLogoutWithNoticeMatch);

export { isRegularLogoutMatch, isLogoutWithNoticeMatch, isAnyLogoutMatch };

export const {
    useSignOutUserMutation,
    useGetSsoUrlQuery,
    useLazyGetSsoUrlQuery,
    useGetApplicationsQuery,
    useGetUserInfoQuery,
    useGetUserSettingsQuery,
    useGetAvailableLocalesQuery,
    useUpdateUserLocaleMutation,
    useUpdateUserSettingsMutation,
    useGetAuthenticatorQuery,
    useInitNewAuthenticatorQuery,
    useUpdateAuthenticatorMutation,
    useVerifyAuthenticatorFromSettingsMutation,
    useActiveUserSessionsQuery,
    useEndAllActiveUserSessionsMutation,
    useVerifyPasswordToUpdatePhoneMutation,
    useInitUpdatePhoneMutation,
    useUpdatePhoneVerifyOtpMutation,
    useChangePasswordMutation,
    useGdprDeleteUserMutation,
    useMfaInitMutation,
    useMfaVerifyMutation,
    useVerifyPasswordMutation,
    useUpdateNameMutation,
    useVerifyTotpMutation,
    useFetchUserAliasesQuery,
    useAddAliasMutation,
    useVerifyAliasOtpMutation,
    useAddAliasAsPrimaryMutation,
    useDeleteAliasMutation,
    useUploadProfileImageMutation,
    useVerifyUriMutation
} = accountApiSlice;
